import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import useLogout from '../Auth/useLogout';
import Dataloader from '../../Component/Dataloader';
import { toast } from 'react-toastify';

export default function StaffEdit() {
    const navigate = useNavigate();
    const logout = useLogout();
    const { id } = useParams(); // Get staff ID from URL parameters

    const [loading, setLoading] = useState(false);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    const [staffDataLoading, setStaffDataLoading] = useState(true);
    const [availablePermissions, setAvailablePermissions] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        role: '',
        permission: [{}]
    });

    useEffect(() => {
        if (!id) {
            toast.error('Invalid staff ID');
            navigate('/staff-list');
            return;
        }
        
        // Fetch staff data by ID
        const fetchStaffData = async () => {
            try {
                setStaffDataLoading(true);
                const response = await axios.get(`${config.apiUrl}/staff/${id}`, {
                    headers: {
                        'Authorization': config.token,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data.success) {
                    const staffData = response.data.data;
                    setFormData({
                        name: staffData.name,
                        email: staffData.email,
                        phone: staffData.phone,
                        role: staffData.role,
                        permission: staffData.permission
                    });
                } else {
                    toast.error(response.data.message || 'Failed to fetch staff data');
                    navigate('/staff-list');
                }
            } catch (error) {
                console.error('Error fetching staff data:', error);
                if (error.response?.status === 401) {
                    logout();
                } else {
                    toast.error('Failed to load staff data');
                    navigate('/staff-list');
                }
            } finally {
                setStaffDataLoading(false);
            }
        };

        // Fetch permissions from config API
        const fetchPermissions = async () => {
            try {
                setPermissionsLoading(true);
                const response = await axios.get(`${config.apiUrl}/config`, {
                    headers: {
                        'Authorization': config.token,
                        'Content-Type': 'application/json'
                    }
                });
                if (response.data.success && response.data.permission && response.data.customer_permission) {
                    if (config.user && config.user.type === 'seller') {
                        setAvailablePermissions(response.data.permission);
                    }
                    if(config.user && config.user.type === 'customer'){
                        setAvailablePermissions(response.data.customer_permission);
                    }
                } else {
                    toast.error('Failed to load permissions configuration');
                }
            } catch (error) {
                console.error('Error fetching permissions:', error);
                if (error.response?.status === 401) {
                    logout();
                } else {
                    toast.error('Failed to load permissions');
                }
            } finally {
                setPermissionsLoading(false);
            }
        };
        
        // Fetch both permissions and staff data
        Promise.all([fetchPermissions(), fetchStaffData()]);
    }, [id, logout, navigate]);

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle permission changes
    const handlePermissionChange = (category, permission, value) => {
        setFormData(prev => ({
            ...prev,
            permission: [{
                ...prev.permission[0],
                [category]: {
                    ...prev.permission[0][category],
                    [permission]: value
                }
            }]
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.name || !formData.email || !formData.phone) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (!formData.email.includes('@')) {
            toast.error('Please enter a valid email address');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.put(`${config.apiUrl}/staff/${id}`, formData, {
                headers: {
                    'Authorization': config.token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                toast.success('Staff member updated successfully!');
                navigate('/staff-list'); // Redirect back to staff list
            } else {
                toast.error(response.data.message || 'Failed to update staff member');
            }
        } catch (error) {
            console.error('Error updating staff:', error);
            if (error.response?.status === 401) {
                logout();
            } else {
                toast.error(error.response?.data?.message || 'Failed to update staff member');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        navigate('/staff-list');
    };

    if (permissionsLoading || staffDataLoading) {
        return <Dataloader />;
    }

    return (
        <div className="container-fluid p-4">
            {/* Header Section */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="fw-bold text-dark mb-1">
                                <i className="fas fa-user-edit me-2 text-primary"></i>
                                Edit Staff Member
                            </h2>
                            <p className="text-muted mb-0">Update staff member information and permissions</p>
                        </div>
                        <button
                            className="btn btn-outline-secondary btn-sm rounded-pill"
                            onClick={handleCancel}
                        >
                            <i className="fas fa-arrow-left me-1"></i>
                            Back to Staff List
                        </button>
                    </div>
                </div>
            </div>

            {/* Form Section */}
            <div className="row">
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-4">
                            <form onSubmit={handleSubmit}>
                                {/* Basic Information */}
                                <div className="mb-4">
                                    <h5 className="fw-semibold text-dark mb-3">
                                        <i className="fas fa-info-circle me-2 text-info"></i>
                                        Basic Information
                                    </h5>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-medium">
                                                <i className="fas fa-user me-1 text-muted"></i>
                                                Full Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                placeholder="Enter staff member's name"
                                                required
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-medium">
                                                <i className="fas fa-envelope me-1 text-muted"></i>
                                                Email Address <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder="Enter email address"
                                                required
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-medium">
                                                <i className="fas fa-phone me-1 text-muted"></i>
                                                Phone Number <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                placeholder="Enter phone number"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-medium">
                                                <i className="fas fa-briefcase me-1 text-muted"></i>
                                                Role <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="role"
                                                value={formData.role}
                                                onChange={handleInputChange}
                                                placeholder="Enter staff role"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Permissions */}
                                {availablePermissions && formData.permission && formData.permission[0] && (
                                    <div className="mb-4">
                                        <h5 className="fw-semibold text-dark mb-3">
                                            <i className="fas fa-shield-alt me-2 text-warning"></i>
                                            Permissions
                                        </h5>

                                        {/* Product Permissions - Only for Seller */}
                                        {config.user && config.user.type === 'seller' && availablePermissions.product && (
                                            <div className="card border-light mb-3">
                                                <div className="card-header bg-light py-2">
                                                    <h6 className="mb-0 fw-semibold">
                                                        <i className="fas fa-box me-2 text-primary"></i>
                                                        Product Management
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row">
                                                        {Object.keys(availablePermissions.product).map(permission => (
                                                            <div key={permission} className="col-md-3 col-sm-6 mb-2">
                                                                <div className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={(formData.permission[0].product && formData.permission[0].product[permission]) || false}
                                                                        onChange={(e) => handlePermissionChange('product', permission, e.target.checked)}
                                                                        id={`product_${permission}`}
                                                                    />
                                                                    <label className="form-check-label text-capitalize" htmlFor={`product_${permission}`}>
                                                                        {permission}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Quotation Permissions - For both Seller and Customer */}
                                        {availablePermissions.quotation && (
                                            <div className="card border-light mb-3">
                                                <div className="card-header bg-light py-2">
                                                    <h6 className="mb-0 fw-semibold">
                                                        <i className="fas fa-file-invoice me-2 text-success"></i>
                                                        Quotation Management
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row">
                                                        {Object.keys(availablePermissions.quotation).map(permission => (
                                                            <div key={permission} className="col-md-3 col-sm-6 mb-2">
                                                                <div className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={(formData.permission[0].quotation && formData.permission[0].quotation[permission]) || false}
                                                                        onChange={(e) => handlePermissionChange('quotation', permission, e.target.checked)}
                                                                        id={`quotation_${permission}`}
                                                                    />
                                                                    <label className="form-check-label text-capitalize" htmlFor={`quotation_${permission}`}>
                                                                        {permission}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Order Permissions - For both Seller and Customer */}
                                        {availablePermissions.order && (
                                            <div className="card border-light mb-3">
                                                <div className="card-header bg-light py-2">
                                                    <h6 className="mb-0 fw-semibold">
                                                        <i className="fas fa-shopping-cart me-2 text-info"></i>
                                                        Order Management
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row">
                                                        {Object.keys(availablePermissions.order).map(permission => (
                                                            <div key={permission} className="col-md-3 col-sm-6 mb-2">
                                                                <div className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={(formData.permission[0].order && formData.permission[0].order[permission]) || false}
                                                                        onChange={(e) => handlePermissionChange('order', permission, e.target.checked)}
                                                                        id={`order_${permission}`}
                                                                    />
                                                                    <label className="form-check-label text-capitalize" htmlFor={`order_${permission}`}>
                                                                        {permission}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Wallet Permissions - For both Seller and Customer */}
                                        {availablePermissions.wallet && (
                                            <div className="card border-light mb-3">
                                                <div className="card-header bg-light py-2">
                                                    <h6 className="mb-0 fw-semibold">
                                                        <i className="fas fa-wallet me-2 text-warning"></i>
                                                        Wallet Management
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row">
                                                        {Object.keys(availablePermissions.wallet).map(permission => (
                                                            <div key={permission} className="col-md-3 col-sm-6 mb-2">
                                                                <div className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={(formData.permission[0].wallet && formData.permission[0].wallet[permission]) || false}
                                                                        onChange={(e) => handlePermissionChange('wallet', permission, e.target.checked)}
                                                                        id={`wallet_${permission}`}
                                                                    />
                                                                    <label className="form-check-label" htmlFor={`wallet_${permission}`}>
                                                                        {permission.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Form Actions */}
                                <div className="d-flex justify-content-end gap-3 mt-4">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary rounded-pill px-4"
                                        onClick={handleCancel}
                                        disabled={loading}
                                    >
                                        <i className="fas fa-times me-1"></i>
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary rounded-pill px-4"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save me-1"></i>
                                                Update Staff Member
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}